import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  FormControlLabel,
  Divider,
  Chip,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clearError } from '../../store/strategistSlice';
import { createAC, fetchAC } from '../../store/strategistSlice';
import { searchTickets } from '../../store/ticketSlice';
import { AC_FORMATS } from '../../shared/appConstants';
import { parseACText } from './strategistAgentUtils';
import { toast } from 'react-toastify';
import OverflowTooltip from '../../shared/OverflowTooltip';
import './StrategistAgent.scss';

const ACGenerator = ({
  taskId,
  taskNumber,
  taskTitle,
  projectId,
  onACGenerated,
  clearExistingAC = false,
  resetTrigger,
  refreshTrigger,
  headerTitle,
  hideFormat = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.strategist);
  const { loading, acceptanceCriteria } = useSelector((state) => state.strategist);

  const [format, setFormat] = useState(AC_FORMATS.GIVEN_WHEN_THEN);
  const [includeEdgeCases, setIncludeEdgeCases] = useState(true);
  const [generatedAC, setGeneratedAC] = useState(null);
  const [ticketTitle, setTicketTitle] = useState(null);
  const [mockTaskId, setMockTaskId] = useState(taskId || '');
  const [ticketOptions, setTicketOptions] = useState([]);
  const [suggestedTickets, setSuggestedTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [hasUserClearedSelection, setHasUserClearedSelection] = useState(false);
  const [lastProjectId, setLastProjectId] = useState(null);
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState(0);
  const getTicketNumber = useCallback((option) => (
    option?.number || option?.ticket_number || option?.ticketNumber || ''
  ), []);

  const loadInitialTickets = useCallback(async () => {
    if (projectId) {
      setLoadingSuggested(true);
      try {
        const result = await dispatch(searchTickets({ projectId, searchTerm: '' })).unwrap();
        setSuggestedTickets(result.slice(0, 10)); // Limit to 10 items
      } catch (error) {
        setSuggestedTickets([]);
      }
      setLoadingSuggested(false);
    }
  }, [projectId, dispatch]);

  const clearACData = useCallback(() => {
    setGeneratedAC(null);
    setTicketTitle(null);
    setSelectedTicket(null);
    setMockTaskId(taskId || '');
    setSearchTerm('');
    setTicketOptions([]);
    // Don't clear suggestedTickets on reset - only on project change
  }, [taskId]);

  // Load existing AC data on mount and clear on project change
  useEffect(() => {
    if (projectId && projectId !== lastProjectId) {
      clearACData();
      setSuggestedTickets([]); // Clear tickets on project change
      setLastProjectId(projectId);
      if (!clearExistingAC) {
        dispatch(fetchAC(projectId));
      }
    }
  }, [projectId, dispatch, taskId, clearACData, clearExistingAC, lastProjectId]);

  // Handle reset trigger
  useEffect(() => {
    if (resetTrigger > 0) {
      clearACData();
      setFormat(AC_FORMATS.GIVEN_WHEN_THEN);
      setIncludeEdgeCases(true);
      setHasUserClearedSelection(false);
      // Don't reload tickets on reset - keep existing suggestedTickets
    }
  }, [resetTrigger, clearACData]);

  // Handle refresh trigger - reload tickets and AC data
  useEffect(() => {
    if (refreshTrigger > 0 && refreshTrigger !== lastRefreshTrigger && projectId) {
      setLastRefreshTrigger(refreshTrigger);
      
      // Reload tickets on refresh using the same function
      loadInitialTickets();
      
      // Reload AC data if not clearing existing AC
      if (!clearExistingAC) {
        dispatch(fetchAC(projectId));
      }
    }
  }, [refreshTrigger, projectId, dispatch, clearExistingAC, lastRefreshTrigger, loadInitialTickets]);

  // Load initial tickets on mount
  useEffect(() => {
    loadInitialTickets();
  }, [loadInitialTickets]);

  // Search tickets when search term changes
  useEffect(() => {
    const delayedSearch = setTimeout(async () => {
      if (searchTerm.length >= 1) {
        setSearching(true);
        try {
          const result = await dispatch(searchTickets({ projectId, searchTerm })).unwrap();
          setTicketOptions(result.slice(0, 10)); // Limit to 10 items
        } catch (error) {
          console.error('Search tickets failed:', error);
          setTicketOptions([]);
        }
        setSearching(false);
      } else {
        setTicketOptions([]);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, projectId, dispatch]);

  useEffect(() => {
    if (acceptanceCriteria?.data && !clearExistingAC) {
      const { data } = acceptanceCriteria;
      
      // If data is empty object, clear any existing generated AC
      if (Object.keys(data).length === 0) {
        setGeneratedAC(null);
        setTicketTitle(null);
        return;
      }
      
      // Check for failed status or errors
      if (data.status === 'FAILED' || (data.agent_output && (data.agent_output.error || data.agent_output.parseError))) {
        setGeneratedAC({
          status: 'failed',
          error: data.agent_output?.error || data.agent_output?.parseError || 'Failed to generate acceptance criteria',
          ticketTitle: data.ticket_title,
          ticketNumber: data.ticket_number
        });
        setTicketTitle(data.ticket_title);
        return;
      }
      
      // Map API format to UI format with fallback
      const uiFormat = data.format === 'bullet points' ? 'bullet' : (data.format || AC_FORMATS.BULLET);
      
      // Check if agent is still generating (status PENDING and no agent_output)
      if (data.status === 'PENDING' && !data.agent_output) {
        // Show generating message with ticket details
        setGeneratedAC({
          status: 'generating',
          message: 'Agent is currently generating acceptance criteria',
          ticketTitle: data.ticket_title,
          ticketNumber: data.ticket_number,
          format: uiFormat
        });
        setTicketTitle(data.ticket_title);
        setFormat(uiFormat);
        return;
      }
      
      // If we have agent output, display it
      if (data.agent_output?.text) {
        const acData = {
          format: uiFormat,
          criteria: parseACText(data.agent_output.text, uiFormat),
          ticketTitle: data.ticket_title,
          ticketNumber: data.ticket_number,
          status: data.status
        };
        
        setGeneratedAC(acData);
        setTicketTitle(data.ticket_title);
        setFormat(uiFormat);
      }
    } else if (clearExistingAC) {
      // Clear existing AC data when clearExistingAC is true
      setGeneratedAC(null);
      setTicketTitle(null);
    }
  }, [acceptanceCriteria, clearExistingAC]);

  // Parse AC text based on format
  // Moved to strategistAgentUtils.js
  useEffect(() => {
    if (taskId && !selectedTicket && !hasUserClearedSelection) {
      // If taskId is provided as prop, set it as the selected ticket
      // Check if this ticket exists in the current project's tickets
      const ticketExists = suggestedTickets.some(ticket => ticket.id === taskId);
      
      if (ticketExists || suggestedTickets.length === 0) {
        // Either ticket exists or we haven't loaded tickets yet
        setSelectedTicket({ 
          id: taskId, 
          number: taskNumber || taskId, 
          title: taskTitle || '' 
        });
        setMockTaskId(taskId);
      } else {
        // Ticket not found in current project
        setSelectedTicket({ 
          id: taskId, 
          number: taskNumber || taskId, 
          title: `${taskTitle || 'Ticket not found in current project'}` 
        });
        setMockTaskId(taskId);
      }
    }
  }, [taskId, taskNumber, taskTitle, selectedTicket, suggestedTickets, hasUserClearedSelection]);

  // Regenerate AC when format changes if AC was already generated
  const handleFormatChange = async (newFormat) => {
    setFormat(newFormat);
    
    // If AC was already generated, regenerate with new format
    if (generatedAC) {
      const effectiveTaskId = taskId || (selectedTicket?.id) || mockTaskId;
      const taskIdToUse = effectiveTaskId || 'default-task-id';
      
      if (taskIdToUse) {
        try {
          await dispatch(
            createAC({
              projectId,
              taskId: taskIdToUse,
              format: newFormat,
              includeEdgeCases,
            })
          ).unwrap();
        } catch (err) {
          console.error('Failed to regenerate AC:', err);
        }
      }
    }
  };

  const handleGenerate = async () => {
    const effectiveTaskId = taskId || (selectedTicket?.id) || mockTaskId;
    
    if (!effectiveTaskId) {
      toast.error(t('agent.ac.taskRequired'));
      return;
    }

    try {
      // Create AC request
      await dispatch(
        createAC({
          projectId,
          taskId: effectiveTaskId,
          format,
          includeEdgeCases,
        })
      ).unwrap();

      // Fetch the AC data after creation
      const result = await dispatch(fetchAC(projectId)).unwrap();
      
      if (result?.data) {
        const { data } = result;
        
        // Check if agent is still generating
        if (data.status === 'PENDING' && !data.agent_output) {
          setGeneratedAC({
            status: 'generating',
            message: 'Agent is currently generating acceptance criteria...'
          });
          toast.info('AC generation started. Please wait...');
        } else if (data.agent_output?.text) {
          // Parse and display the generated AC
          const acData = {
            format: data.format || format,
            criteria: parseACText(data.agent_output.text, data.format || format),
            ticketTitle: data.ticket_title,
            ticketNumber: data.ticket_number,
            status: data.status
          };
          
          setGeneratedAC(acData);
          setTicketTitle(data.ticket_title);
          onACGenerated?.(acData);
          toast.success(t('agent.ac.generated'));
        }
      }
    } catch (err) {
      console.error('Failed to generate AC:', err);
    }
  };

  const renderACPreview = () => {
    if (!generatedAC) return null;

    // Handle failed state
    if (generatedAC.status === 'failed') {
      return (
        <Paper elevation={2} className="breakdown-preview-paper">
          <Alert severity="error" className="breakdown-preview-error-alert">
            {t('agent.breakdown.generationFailed')}
            {generatedAC.error && (
              <>: {generatedAC.error}</>
            )}
          </Alert>
        </Paper>
      );
    }

    // Handle generating state
    if (generatedAC.status === 'generating') {
      return (
        <Box className="ac-generating-container">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1">
              {generatedAC.message || 'Agent is currently generating acceptance criteria...'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This page will update automatically once generation is complete
            </Typography>
          </Box>
        </Box>
      );
    }

    // Use the format from generatedAC if available, otherwise fallback to bullet
    const currentFormat = generatedAC.format || AC_FORMATS.BULLET;

    if (currentFormat === AC_FORMATS.GIVEN_WHEN_THEN) {
      return (
        <Box className="ac-preview-content">
          {generatedAC?.criteria?.map((criterion, idx) => (
            <Paper key={idx} elevation={0} className="ac-criterion-card">
              <Box className="ac-criterion-section">
                <Chip label={t('agent.ac.given')} size="small" className="ac-criterion-label ac-chip-given" />
                <Typography variant="body2" className="ac-criterion-text">
                  {criterion.given}
                </Typography>
              </Box>
              <Box className="ac-criterion-section">
                <Chip label={t('agent.ac.when')} size="small" className="ac-criterion-label ac-chip-when" />
                <Typography variant="body2" className="ac-criterion-text">
                  {criterion.when}
                </Typography>
              </Box>
              <Box className="ac-criterion-section">
                <Chip label={t('agent.ac.then')} size="small" className="ac-criterion-label ac-chip-then" />
                <Typography variant="body2" className="ac-criterion-text">
                  {criterion.then}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    if (currentFormat === AC_FORMATS.BULLET || currentFormat === AC_FORMATS.CHECKLIST) {
      return (
        <Box className="ac-preview-content">
          <Paper elevation={0} className={`ac-list-card ${currentFormat === AC_FORMATS.CHECKLIST ? 'ac-checklist-card' : ''}`}>
            {generatedAC?.criteria?.map((item, idx) => {
              // Handle different data structures
              let displayText = '';
              if (typeof item === 'string') {
                displayText = item;
              } else if (item && typeof item === 'object') {
                // If it's a Given-When-Then object, convert it to a readable format
                if (item.given && item.when && item.then) {
                  displayText = `Given ${item.given}, when ${item.when}, then ${item.then}`;
                } else if (item.description) {
                  displayText = item.description;
                } else {
                  // Fallback: stringify the object
                  displayText = JSON.stringify(item);
                }
              } else {
                displayText = String(item);
              }

              if (currentFormat === AC_FORMATS.CHECKLIST) {
                return (
                  <Box key={idx} className="ac-checklist-item">
                    <Box className="ac-checklist-indicator" />
                    <Typography variant="body1" className="ac-checklist-text">
                      {displayText}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box key={idx} className="ac-list-item">
                  <Typography variant="body1" className="ac-list-text">
                    {displayText}
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box className="ac-generator-container">
      <Paper elevation={0} className="ac-generator-paper">
        <Box className="ac-generator-header">
          <Box className="ac-generator-header-content">
            <Typography variant="h6" className="ac-generator-header-title ac-generator-header-title-styled">
              {headerTitle || t('agent.ac.title')}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" className="ac-generator-error" onClose={() => dispatch(clearError())}>
            {error.message || error}
          </Alert>
        )}

        <Box className="ac-generator-form">
          <Box className="ac-generator-form-row">
            {!hideFormat && (
              <Box className="ac-generator-form-field-group">
                <Typography variant="body2" className="ac-generator-field-label">
                  {t('agent.ac.format')}
                </Typography>
                <FormControl className="ac-generator-form-control">
                  <Select 
                    value={format} 
                    onChange={(e) => handleFormatChange(e.target.value)}
                    displayEmpty
                    disabled={generatedAC}
                  >
                    <MenuItem value={AC_FORMATS.GIVEN_WHEN_THEN}>{t('agent.ac.formatGivenWhenThen')}</MenuItem>
                    <MenuItem value={AC_FORMATS.BULLET}>{t('agent.ac.formatBullet')}</MenuItem>
                    <MenuItem value={AC_FORMATS.CHECKLIST}>{t('agent.ac.formatChecklist')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box className="ac-generator-form-field-group">
              <Typography variant="body2" className="ac-generator-field-label">
                {t('agent.ac.taskId')}
              </Typography>
              <Autocomplete
                options={searchTerm.length >= 1 ? ticketOptions : suggestedTickets}
                value={generatedAC ? { id: generatedAC.ticketNumber, number: generatedAC.ticketNumber, title: generatedAC.ticketTitle } : selectedTicket}
                onChange={(event, newValue) => {
                  setSelectedTicket(newValue);
                  setMockTaskId(newValue?.id || '');
                  if (!newValue) {
                    setSearchTerm('');
                    setHasUserClearedSelection(true);
                  } else {
                    setHasUserClearedSelection(false);
                    // Clear search term when option is selected to prevent API call
                    setSearchTerm('');
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason !== 'reset' && reason !== 'clear') {
                    setSearchTerm(newInputValue);
                  }
                }}
                filterOptions={(x) => x}
                disabled={generatedAC}
                loading={searching || loadingSuggested}
                clearOnEscape
                disableClearable={false}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    const optionNumber = getTicketNumber(option);
                    return optionNumber && option?.title 
                      ? `${optionNumber} · ${option.title}` 
                      : optionNumber || option?.title || '';
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0, flexShrink: 0 }}>
                          {getTicketNumber(option)}
                        </Typography>
                        <OverflowTooltip
                          title={option.title}
                          tooltipWidth={400}
                          variant="body2"
                          maxLines={1}
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          {option.title}
                        </OverflowTooltip>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => {
                    const selectedValue = generatedAC ? 
                      { id: generatedAC.ticketNumber, number: generatedAC.ticketNumber, title: generatedAC.ticketTitle } : 
                      selectedTicket;
                    
                    const selectedNumber = getTicketNumber(selectedValue);
                    const displayValue = selectedNumber && selectedValue?.title 
                      ? `${selectedNumber} · ${selectedValue.title}` 
                      : selectedNumber || selectedValue?.title || '';
                    
                    return (
                      <OverflowTooltip
                        title={displayValue}
                        tooltipWidth={400}
                        placement="top-start"
                        component="div"
                        sx={{ width: '100%' }}
                      >
                        <TextField
                          {...params}
                          className="ac-generator-task-input"
                          placeholder={t('agent.ac.taskIdPlaceholder') || 'Search for a task...'}
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              '& input': {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }
                            },
                            endAdornment: (
                              <>
                                {searching || loadingSuggested ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      </OverflowTooltip>
                    );
                  }}
                noOptionsText={searchTerm.length >= 1 ? t('agent.ac.noTasksFound') : (loadingSuggested ? '' : t('agent.ac.noSuggestedTasks'))}
                className="ac-generator-task-autocomplete"
              />
              {selectedTicket && selectedTicket.title && selectedTicket.title.includes('not found in current project') && (
                <Typography variant="caption" color="error" className="ac-generator-ticket-warning">
                  {t('agent.ac.ticketNotFoundInProject')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box className="ac-generator-options-section">
          <FormControlLabel
            control={
              <Checkbox 
                checked={includeEdgeCases} 
                onChange={(e) => setIncludeEdgeCases(e.target.checked)}
                size="small"
                className="ac-generator-checkbox-input"
                disabled={generatedAC}
              />
            }
            label={t('agent.ac.includeEdgeCases')}
            className="ac-generator-checkbox"
          />
        </Box>

        {!generatedAC && (
          <Box className="ac-generator-actions-row">
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading.createAC || !selectedTicket?.id}
              className="ac-generator-button"
            >
              {loading.createAC ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('agent.ac.generating')}
                </>
              ) : (
                t('agent.ac.generate')
              )}
            </Button>
          </Box>
        )}

        {generatedAC && (
          <>
            <Divider className="ac-generator-divider" />
            <Box className="ac-preview-section">
              <Box className="ac-preview-header">
                <Typography variant="subtitle1" className="ac-preview-title">
                  {generatedAC.status === 'generating' ? 'Generation in Progress' : t('agent.ac.preview')}
                </Typography>
              </Box>

              {(ticketTitle || generatedAC.ticketTitle) && (
                <Box className="ac-ticket-title-section">
                  <Typography variant="body2" className="ac-ticket-title-label">
                    {t('agent.ac.ticketTitle')}:
                  </Typography>
                  <Typography variant="subtitle2" className="ac-ticket-title-text">
                    {generatedAC.ticketNumber && `${generatedAC.ticketNumber} - `}{ticketTitle || generatedAC.ticketTitle}
                  </Typography>
                </Box>
              )}

              {generatedAC.status !== 'generating' && generatedAC.status !== 'failed' && (
                <Alert 
                  severity="success" 
                  icon={<Info className="ac-save-info-icon" />}
                  className="ac-save-info-alert"
                >
                  <Typography variant="body2" className="ac-save-info-text">
                    Acceptance criteria has been automatically saved to the ticket.
                  </Typography>
                </Alert>
              )}

              {renderACPreview()}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ACGenerator;
